import { useEffect, useState } from "react";
import snakeCase from "voca/snake_case";
import { heapEvent } from "./heapEvent";
import { useFlags } from "./useFlags";
import { useHeaperIdentity } from "./useHeaperIdentity";
import { setTracerUser } from "../AnalyticsUtilities/Logging";
import { env } from "../env";
import { featureEnv } from "../featureEnv";
import { IntercomProjectId } from "../types";
/**
 * `useHeaper(PromotionLevel)` should be invoked at the top of your App template.  This hook will call the heaper-service to obtain the `HeaperIdentity` for a user.
 *
 * - For Heap, this hook will `heap.identify()` the user ([docs](https://developers.heap.io/reference/identify)).
 * - For Intercom, this hook will `Intercom('boot')` with identity verification credentials for the user ([docs](https://developers.intercom.com/installing-intercom/docs/enable-identity-verification-on-your-web-product)).
 *
 * Use the options parameter to pass `heap = false` or `intercom = false` if you are not using Heaper for either service.
 */
export const useHeaper = (appName, promotionLevel, { accessToken, idToken, appPlatform, multiPageApp, heap, heapInstance, intercom, intercomInstance, launchDarklyClientSideId, launchDarklyInstance, datadogRumInstance, silent, extendEventProps, internalProducts, getScreenDimensions, }) => {
    const [bootstrapped, setBootstrapped] = useState(false);
    env.setAppName(appName);
    env.setPromotionLevel(promotionLevel);
    if (appPlatform) {
        env.setAppPlatform(appPlatform);
    }
    if (internalProducts !== undefined) {
        env.setInternalProducts(internalProducts);
    }
    env.setAuthHeaders(accessToken, idToken);
    if (multiPageApp !== undefined) {
        env.setMultiPageApp(multiPageApp);
    }
    if (heap !== undefined) {
        env.setHeap(heap);
    }
    if (heapInstance) {
        env.setHeapInstance(heapInstance);
    }
    if (intercom !== undefined) {
        env.setIntercom(intercom);
    }
    if (intercomInstance) {
        env.setIntercomInstance(intercomInstance);
    }
    if (launchDarklyClientSideId) {
        env.setLaunchDarklyClientSideId(launchDarklyClientSideId);
    }
    if (launchDarklyInstance) {
        env.setLaunchDarklyInstance(launchDarklyInstance);
    }
    if (launchDarklyClientSideId && launchDarklyInstance) {
        throw new Error("useHeaper() was provided conflicting options.  Provide one of `launchDarklyInstance` or a `launchDarklyClientSideId` value to be used to create an instance.");
    }
    if (datadogRumInstance) {
        env.setDatadogRumInstance(datadogRumInstance);
    }
    if (extendEventProps) {
        env.setExtendEventProps(extendEventProps);
    }
    if (getScreenDimensions) {
        env.setScreenDimensionsGetter(getScreenDimensions);
    }
    if (silent !== undefined) {
        env.setSilent(silent);
    }
    const heaperIdentity = useHeaperIdentity();
    const flags = useFlags();
    const hasBrowserPerformanceEntries = Boolean(typeof window?.performance?.getEntriesByType === "function" &&
        window?.performance?.getEntriesByType("navigation").length > 0);
    const device = hasBrowserPerformanceEntries ? "Browser" : "Mobile App";
    useEffect(() => {
        if (!bootstrapped) {
            if (env.getLaunchDarklyClientSideId() && heaperIdentity && !flags) {
                featureEnv.boot(heaperIdentity);
            }
            else if (heaperIdentity) {
                const { unique_identity, user_id, user_hash } = heaperIdentity;
                // identify with Datadog RUM, if required
                const ddTracer = env.getDatadogRumInstance();
                if (ddTracer) {
                    setTracerUser(ddTracer, heaperIdentity);
                }
                // setup intercom first--we will send an intercom event using heapEvent after
                if (env.getIntercom()) {
                    try {
                        // throw an error and break if intercom misconfigured
                        env.validateIntercomEnv();
                        if (env.getIntercomInstance()) {
                            env.getIntercomInstance()("boot", {
                                app_id: IntercomProjectId[promotionLevel],
                                user_id,
                                user_hash,
                            });
                        }
                    }
                    catch (err) {
                        console.error(err);
                    }
                }
                if (env.getHeap()) {
                    try {
                        // throw an error and break if heap misconfigured
                        env.validateHeapEnv();
                        if (env.getHeapInstance()) {
                            env.getHeapInstance().identify(unique_identity);
                        }
                        // fire if SPA or no existing MPA session
                        if (env.getMultiPageApp() === false ||
                            (env.getMultiPageApp() === true &&
                                env.hasCurrentMultiPageAppSession() === false)) {
                            heapEvent(`${appName} - ${device} - Load - App`, hasBrowserPerformanceEntries
                                ? Object.fromEntries(Object.entries(window.performance
                                    ?.getEntriesByType("navigation")
                                    ?.shift()
                                    ?.toJSON())
                                    .map(([k, v]) => [
                                    snakeCase(k),
                                    !Array.isArray(v) ? v : undefined,
                                ])
                                    .filter(([, v]) => v !== undefined))
                                : {}, env.getIntercom());
                        }
                        // update existing MPA session timestamp if required
                        env.updateCurrentMultiPageAppSession();
                    }
                    catch (err) {
                        console.error(err);
                    }
                }
                setBootstrapped(true);
                env.drainHeapEventQueue();
            }
        }
    }, [heaperIdentity, flags]);
    useEffect(() => {
        if (heaperIdentity && env.getIntercom()) {
            env.getIntercomInstance()("update");
        }
    }, [window?.location]);
};
//# sourceMappingURL=useHeaper.js.map